import {
  BrowserModule,
  provideClientHydration,
  withEventReplay,
  withHttpTransferCacheOptions,
  withIncrementalHydration,
} from '@angular/platform-browser';
import { NgModule, LOCALE_ID } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { Store, StoreModule } from '@ngrx/store';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { EffectsModule } from '@ngrx/effects';
import { environment } from '../environments/environment';
import { DictionaryConfig, DictionaryNgrxModule } from '@teamfoster/sdk/dictionary-ngrx';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { reducers, CustomSerializer, appEffects, metaReducers } from './store';

import localeNl from './nl';
import localeDE from '@angular/common/locales/de';

import { ViewportScroller, registerLocaleData } from '@angular/common';

// not used in production
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { NotFoundComponent } from './404.component';
import { CookieConfig, CookieNgrxModule } from '@teamfoster/sdk/cookie-ngrx';
import { MenuNgrxModule } from '@teamfoster/sdk/menu-ngrx';
import { ActionBarsModule } from './action-bars/action-bars.module';
import { ShopModule } from './shop/shop.module';
import { DynamicFormConfig, ProvideDynamicForms } from '@teamfoster/dynamic-forms';
import { formControls } from './form/form.module';
import { CartModule } from './cart/cart.module';

import { LanguageNgrxModule, getActiveLanguageNoCulture } from '@teamfoster/sdk/language-ngrx';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { NewslettersModule } from './newsletters/newsletters.module';
import { InViewModule } from '@teamfoster/sdk/in-view';
import { ThemesModule } from './themes/themes.module';

import { SocialMediaModule } from './social-media/social-media.module';

import { NavigationEnd, Router, Scroll, Event } from '@angular/router';
import { filter, pairwise } from 'rxjs';
import { StorefrontsModule } from './storefronts';
import { ImageFlowConfig, ProvideOptimisedImageProcessor } from '@teamfoster/sdk/image';
import { LanguageInterceptor } from './utility/language.interceptor';

import { register } from 'swiper/element/bundle';

register();

const dictConfig: Partial<DictionaryConfig> = {
  mapping: [
    {
      domainPart: '.nl/',
      language: 'nl',
    },
  ],
};

const cookieConfig: CookieConfig = {
  analyticsCode: 'GTM-QXR9',
  useTagManager: true,
  strippedContentPlaceholder: 'Deze video wordt niet getoond omdat je geen marketing cookies hebt geaccepteerd',
  buttonSettings: 'Instellingen',
  buttonAcceptAll: 'Accept all',
  buttonClose: 'Sluiten',
  strippedContentButton: 'Cookie-instellingen wijzigen',
  types: {
    analytical: true,
    functional: true,
    marketing: true,
  },
  showSettings: true,
};

export const dynamicFormConfig: DynamicFormConfig = {
  genericInputErrorMessage: 'Het formulier is niet correct ingevuld, controleer de velden en probeer nogmaals.',
  genericServerErrorMessage:
    'Er is iets fout gegaan bij het versturen van het formulier, probeer het later nogmaals. Blijft het probleem voordoen neem dan contact met ons op.',
  formControls: formControls,
  formControlErrors: {
    pattern: 'Telefoonnummer-error',
    passwordInvalid: 'password-invalid-error',
    // VatNumberInvalid: 'test',
  },
};

const getLocaleId = (store: Store) => {
  return store.selectSignal(getActiveLanguageNoCulture)();
};

registerLocaleData(localeNl, 'nl');
registerLocaleData(localeDE, 'de');
@NgModule({
  declarations: [AppComponent, NotFoundComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    FormsModule,
    AppRoutingModule,
    CoreModule,
    StorefrontsModule,
    ActionBarsModule,
    NewslettersModule,
    CartModule,
    ThemesModule,
    InViewModule,
    StoreModule.forRoot(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
      },
    }),
    EffectsModule.forRoot(appEffects),
    StoreRouterConnectingModule.forRoot({ serializer: CustomSerializer }),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production,
    }),
    DictionaryNgrxModule.forRoot(dictConfig),
    CookieNgrxModule.forRoot(cookieConfig),
    LanguageNgrxModule.forRoot({
      defaultLanguage: 'nl-NL',
      mapping: [
        { domainPart: '.nl/', language: 'nl-NL' },
        { domainPart: '.de/', language: 'de-DE' },
        { domainPart: '.com/', language: 'en-GB' },
        { domainPart: '.fr/', language: 'en-GB' },
        { domainPart: '.co.uk/', language: 'en-GB' },
      ],
    }),
    MenuNgrxModule.forRoot({}),
    SocialMediaModule,
    BrowserAnimationsModule,
    ShopModule,
  ],
  providers: [
    { provide: LOCALE_ID, useFactory: getLocaleId, deps: [Store] },
    { provide: HTTP_INTERCEPTORS, useClass: LanguageInterceptor, multi: true },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'fill', floatLabel: 'always' } },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 1500 } },
    ProvideDynamicForms(dynamicFormConfig),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    ProvideOptimisedImageProcessor(ImageFlowConfig, {}),
    provideClientHydration(withIncrementalHydration()),
  ],
})
export class AppModule {
  // Bugfix: scrollPositionRestoration enabled would scroll to top when params change on same page
  constructor(router: Router, viewportScroller: ViewportScroller) {
    router.events
      .pipe(
        filter((e: Event): e is Scroll => e instanceof Scroll),
        pairwise()
      )
      .subscribe(eventPair => {
        const previousEvent = eventPair[0];
        const event = eventPair[1];
        if (event.position) {
          // backward navigation
          viewportScroller.scrollToPosition(event.position);
        } else if (event.anchor) {
          // anchor navigation
          viewportScroller.scrollToAnchor(event.anchor);
        } else if (event.routerEvent instanceof NavigationEnd && previousEvent.routerEvent instanceof NavigationEnd) {
          // forward navigation
          if (previousEvent.routerEvent.urlAfterRedirects.split('?')[0] !== event.routerEvent.urlAfterRedirects.split('?')[0]) {
            // Routes don't match, this is actual forward navigation
            // Default behavior: scroll to top
            viewportScroller.scrollToPosition([0, 0]);
          }
        }
      });
  }
}
